import React, { useState, useCallback, useEffect, useMemo, Suspense, lazy } from "react";

// @ts-ignore
import cn from "classnames";
import { useQuery, useMutation } from "@apollo/client";
import { useHistory, useLocation } from "react-router-dom";
import { useFlag } from "@unleash/proxy-client-react";
import { AxiosError } from "axios";

import { FacebookPixel } from "react-use-facebook-pixel";
import { DelayNextBoxProvider } from "../../molecules/DelayNextBox/context";
import {
  GET_GUEST_PROMO_CODE_DISCOUNT,
  GET_LOCAL_SHOPPING_CART,
  GET_MEMBER_LAYOUT_INFO,
  GET_REFERRAL_DISCOUNT,
} from "../../../graphql/queries";
import {
  SUBSCRIPTION_STATUS_NAME_TO_ID,
  HEADER_MENU_NAME_MAX_LENGTH,
  REACT_APP_ITERABLE_API_KEY,
  REACT_APP_ITERABLE_APP_NAME,
  UNLEASH_ITERABLE_MESSAGING,
  ERROR_CODE_ITERABLE_FORGOTTEN_USER,
  REACT_APP_FACEBOOK_PIXEL_ID,
  IS_PROD,
  CHRISTMAS_CUT_OFF_DATES_VIEW_STORAGE_KEY,
  CHRISTMAS_CUT_OFF_DATES_VIEW_STORAGE_KEY_TTL,
  STORE_LOCATION_ID_STORAGE_KEY,
  FETCH_POLICY_CACHE_AND_NETWORK,
} from "../../../helpers/constants";
import ModalContainer from "../../molecules/ModalContainer";

import Footer from "../Footer";
import Header from "../Header";
import { NATIVE_TAB_BAR_HEIGHT } from "../NativeTabBar/constants";
import { shouldHideTabBar } from "../NativeTabBar/visibility";
import ShippingDatesNoticeModal from "../../molecules/ShippingDatesNoticeModal";
import BoxAndCartTray from "../BoxAndCartTray";
import { useDeviceSize, useInitUnleashClient } from "../../../helpers/hooks";
import { pageOwnsPrerenderReady } from "../../../helpers/prerenderReadyOwnership";
import { warmUpStripe } from "../../../helpers/stripeLoader";
import {
  checkIfRequestIsFromMobileApp,
  extractNumberFromString,
  shouldDisplayChristmasCutOffNotice,
  checkIfRequestIsFromStoreLocation,
  shouldResumeBarBeShown,
} from "../../../helpers/tools";
import {
  offerToChangeCountry,
  getCountryRemovedUrl,
  checkIfUrlIsInUrlGroup,
} from "../../../helpers/urls.helper";
import { colors, header } from "../../../styles/variables";
import { getStoreLocationId, isLoggedIn } from "../../../helpers/auth";
import { logError } from "../../../helpers/logging";
import StyledLoading from "../../atoms/Loading/StyledLoading";
import { GuestPromoCodeDiscount } from "../../../types/__generated__/GuestPromoCodeDiscount";
import { ReferralDiscount } from "../../../types/__generated__/ReferralDiscount";
import { MemberLayoutInfo } from "../../../types/__generated__/MemberLayoutInfo";
import { LocalShoppingCart } from "../../../types/__generated__/LocalShoppingCart";
import { RECORD_MEMBER_PHYSICAL_STORE_VISIT } from "../../../graphql/resolvers/member";
import {
  RecordMemberPhysicalStoreVisit,
  RecordMemberPhysicalStoreVisitVariables,
} from "../../../types/__generated__/RecordMemberPhysicalStoreVisit";
import URL_PATTERNS from "../../../urls";
import { getWithExpiry, setWithExpiry } from "../../../helpers/storage";
import { URL_PATTERNS_SHOW_CHRISTMAS_CUT_OFF } from "../../../helpers/urls.constants";
import { isCapacitorNative } from "../../../helpers/isCapacitorNative";
import { GlobalStyle, BoxHolder, PageTransition, TopInsetSpacer } from "./style";
import { AppContext, LayoutContext } from "./context";
import { DEFAULT_NOTIFICATION_LOCATIONS } from "./helper";

// Lazy-loaded: this component renders nothing visible but ships a non-trivial
// amount of code (Apollo helpers, cache-write fragments, toast wiring). Pulling
// it out of the main chunk keeps the initial JS payload smaller, especially for
// logged-out visitors who never render it at all.
const RecommendationExplanationPoller = lazy(() => import("../../molecules/RecommendationExplanationPoller"));

// Modal/banner organisms gated by user interaction or geo/auth state. Lazy-
// loading them shrinks the eager Layout chunk and keeps these out of the
// initial render commit. Each render site is wrapped in <Suspense fallback={null}>.
const LogInPanel = lazy(() => import("../LogInPanel"));
const ChangeLanguagePopup = lazy(() => import("../../molecules/ChangeLanguagePopup"));
const WelcomePackWarning = lazy(() => import("../../molecules/WelcomePackWarning"));
const AnnouncementPopup = lazy(() => import("../AnnouncementPopup"));
const ResumeQuizBar = lazy(() => import("../../atoms/ResumeQuizBar"));

interface LayoutProps {
  isHeaderTransparent?: boolean;
  showAnnouncementBar?: boolean;
  showBoxAndCartTray?: boolean;
  hasTabBarUnderHeader?: boolean;
  showAnnouncementPopupForSubscribers?: boolean;
  showAnnouncementPopupForNonSubscribers?: boolean;
  /** Pages with their own navigation (e.g. the store landing page) hide the
   * shared site header while keeping the rest of the Layout machinery. */
  hideHeader?: boolean;
  /** Pages with their own footer (e.g. the store landing page) hide the shared
   * site footer (and the resume-quiz bar that mounts alongside it). */
  hideFooter?: boolean;
  className?: string;
  children: React.ReactNode;
  variant?: "home" | "standard";
}

const Layout = ({
  showBoxAndCartTray = true,
  showAnnouncementBar = false,
  showAnnouncementPopupForSubscribers = false,
  showAnnouncementPopupForNonSubscribers = false,
  hasTabBarUnderHeader = false,
  hideHeader = false,
  hideFooter = false,
  className,
  children,
  variant = "standard",
}: LayoutProps) => {
  const { pathname } = useLocation();
  const userIsLoggedIn = isLoggedIn();

  const { isSmall } = useDeviceSize();

  const isIterableMessagingEnabled = useFlag(UNLEASH_ITERABLE_MESSAGING);

  const hasUserSeenChristmasCutOffPopup = !getWithExpiry(CHRISTMAS_CUT_OFF_DATES_VIEW_STORAGE_KEY);
  const [showShippingDatesNotice, setShowShippingDatesNotice] = useState<boolean>(false);

  const [showHeaderBarFromChild, setShowHeaderBarFromChild] = useState<boolean>(false);
  const [shouldOpenBoxAndCartTray, setShouldOpenBoxAndCartTray] = useState<boolean>(false);

  /* Top-inset bg stacks — see Layout/context.ts for semantics. Two
   * stacks so a structural wrapper's "default" push (TrayContainer
   * white, Layout's cart-strip coral) doesn't bury an inner hero's
   * explicit push (MainBottleInfo's wine colour) when React fires the
   * wrapper's effect after the leaf's. Stable callbacks via functional
   * updates so the use*-Bg hooks' effect deps don't churn. */
  const [topInsetExplicitStack, setTopInsetExplicitStack] = useState<{ id: string; color: string }[]>([]);
  const [topInsetDefaultStack, setTopInsetDefaultStack] = useState<{ id: string; color: string }[]>([]);
  const pushTopInsetBg = useCallback((id: string, color: string) => {
    setTopInsetExplicitStack((prev) => [...prev.filter((e) => e.id !== id), { id, color }]);
  }, []);
  const popTopInsetBg = useCallback((id: string) => {
    setTopInsetExplicitStack((prev) => prev.filter((e) => e.id !== id));
  }, []);
  const pushTopInsetBgDefault = useCallback((id: string, color: string) => {
    setTopInsetDefaultStack((prev) => [...prev.filter((e) => e.id !== id), { id, color }]);
  }, []);
  const popTopInsetBgDefault = useCallback((id: string) => {
    setTopInsetDefaultStack((prev) => prev.filter((e) => e.id !== id));
  }, []);
  const activeTopInsetBg =
    topInsetExplicitStack[topInsetExplicitStack.length - 1]?.color ??
    topInsetDefaultStack[topInsetDefaultStack.length - 1]?.color ??
    colors.white;

  const iterableRef = React.useRef<{ logout: Function; setEmail: Function } | null>(null);

  const {
    data: memberLayoutInfoData,
    refetch: refetchMemberLayoutInfo,
    loading: isMemberLayoutInfoLoading,
  } = useQuery<MemberLayoutInfo>(GET_MEMBER_LAYOUT_INFO, {
    variables: {
      skipMe: !userIsLoggedIn,
    },
    /* Matches the RN mobile app's policy for the equivalent layout-info
     * query. Renders instantly from cache on revisit (most Layout
     * consumers read the same handful of fields — header counts, member
     * name, badge totals) while still refreshing in the background. The
     * global default is network-only, which blocks first paint of every
     * page on a fresh round-trip and is especially painful on iOS
     * WKWebView where each request pays a cold TLS handshake through
     * CapacitorHttp. */
    fetchPolicy: FETCH_POLICY_CACHE_AND_NETWORK,
  });

  const { data: localShoppingCartData } = useQuery<LocalShoppingCart>(GET_LOCAL_SHOPPING_CART, {
    skip: userIsLoggedIn,
  });
  const { data: guestMemberDiscountsData } = useQuery<GuestPromoCodeDiscount>(GET_GUEST_PROMO_CODE_DISCOUNT, {
    skip: userIsLoggedIn,
  });
  const { data: guestMemberReferralDiscountsData } = useQuery<ReferralDiscount>(GET_REFERRAL_DISCOUNT, {
    skip: userIsLoggedIn,
  });

  const [recordMemberPhysicalStoreVisit] = useMutation<
    RecordMemberPhysicalStoreVisit,
    RecordMemberPhysicalStoreVisitVariables
  >(RECORD_MEMBER_PHYSICAL_STORE_VISIT);

  const [isBoxCartTrayOpened, setIsBoxCartTrayOpened] = useState<boolean>(false);
  const [heightOfAnnouncement, setHeightOfAnnouncement] = useState<number>(0);
  const [isLoginPanelShown, setIsLoginPanelShown] = useState<boolean>(false);
  const [showWelcomePackWarning, setShowWelcomePackWarning] = useState<boolean>(false);
  const [isHeaderShown, setIsHeaderShown] = useState<boolean>(true);
  const [showOfferToChangeCountry, setShowOfferToChangeCountry] = useState<boolean>(false);
  const [isAnnouncementPopupShown, setIsAnnouncementPopupShown] = useState<boolean>(false);
  const [isIterableReady, setIsIterableReady] = useState<boolean>(false);
  const [facebookPixel, setFacebookPixel] = useState<FacebookPixel | null>(null);

  useInitUnleashClient();

  const handleChangeHeightOfAnnouncement = (height: number) => {
    setHeightOfAnnouncement(
      hasTabBarUnderHeader && !isBoxCartTrayOpened
        ? height + extractNumberFromString(header.tabBarHeight)
        : height
    );
  };

  const handleToggleWelcomePackWarning = () => {
    setIsBoxCartTrayOpened(!showWelcomePackWarning && isBoxCartTrayOpened);
    setShowWelcomePackWarning(!showWelcomePackWarning);
  };

  const checkIfShouldOfferToChangeCountry = useCallback(async () => {
    setShowOfferToChangeCountry(await offerToChangeCountry());
  }, []);

  const setIterableJwtToken = () => {
    const { iterableJwtToken } = memberLayoutInfoData?.me || {};
    return Promise.resolve(iterableJwtToken || "");
  };

  // This effect makes the window scroll to top at every page change.
  useEffect(() => {
    window.scrollTo(0, 0);

    // Since showHeaderBarFromChild is set to true on some pages, set both
    // variables back to false when changing pages, so that the new page does not inherit the old state.
    setShowHeaderBarFromChild(false);

    const shouldNavigateToDashboard = pathname === URL_PATTERNS.HOME && userIsLoggedIn;

    if (shouldNavigateToDashboard) {
      history.push(URL_PATTERNS.MY_DASHBOARD);
    }

    // Chistmas cut off dates view
    const shouldShowChristmasCutOffPage = checkIfUrlIsInUrlGroup(
      pathname,
      URL_PATTERNS_SHOW_CHRISTMAS_CUT_OFF
    );

    const shouldShowShippingDatesNotice =
      shouldShowChristmasCutOffPage &&
      hasUserSeenChristmasCutOffPopup &&
      !showOfferToChangeCountry &&
      shouldDisplayChristmasCutOffNotice();

    if (shouldShowShippingDatesNotice) {
      setShowShippingDatesNotice(shouldShowShippingDatesNotice);
      setWithExpiry(
        CHRISTMAS_CUT_OFF_DATES_VIEW_STORAGE_KEY,
        true,
        CHRISTMAS_CUT_OFF_DATES_VIEW_STORAGE_KEY_TTL
      );
    }
  }, [pathname]);

  // Check if the user should be offered to change country
  useEffect(() => {
    checkIfShouldOfferToChangeCountry();
  }, [checkIfShouldOfferToChangeCountry]);

  // Warm up Stripe.js at idle so Radar gets its browse-time fraud signals
  // without the load competing with boot or poisoning checkout on failure.
  useEffect(() => {
    warmUpStripe();
  }, []);

  // Signal to the prerender that the page content is in the DOM.
  // Skip when a child page has claimed ownership via usePrerenderReady — those
  // pages signal readiness based on their own data, not just member layout info.
  useEffect(() => {
    // eslint-disable-next-line no-underscore-dangle
    if (window.__PRERENDER__ && !isMemberLayoutInfoLoading && !pageOwnsPrerenderReady()) {
      window.prerenderReady = true;
    }
  }, [isMemberLayoutInfoLoading]);

  // Set up Iterable (lazy-loaded)
  useEffect(() => {
    let cancelled = false;
    const isMemberLayoutDataLoaded = !isMemberLayoutInfoLoading && !!memberLayoutInfoData?.me;
    const { email } = memberLayoutInfoData?.me || {};
    /* The web build uses @iterable/web-sdk here for its in-app + tracking
     * flow. (Native Capacitor Iterable wiring is currently disconnected.) */
    const shouldSetUserIterableEmail =
      isMemberLayoutDataLoaded &&
      userIsLoggedIn &&
      isIterableMessagingEnabled &&
      email &&
      !isCapacitorNative();

    if (shouldSetUserIterableEmail) {
      import("@iterable/web-sdk").then(({ initializeWithConfig }) => {
        if (cancelled) return;
        if (!iterableRef.current) {
          iterableRef.current = initializeWithConfig({
            authToken: REACT_APP_ITERABLE_API_KEY,
            configOptions: { dangerouslyAllowJsPopups: true },
            generateJWT: () => setIterableJwtToken(),
          });
        }
        iterableRef.current.setEmail(email).then(() => {
          if (!cancelled) {
            setIsIterableReady(true);
          }
        });
      });
    }

    return () => {
      cancelled = true;
    };
  }, [memberLayoutInfoData, userIsLoggedIn]);

  // Log out from Iterable
  useEffect(() => {
    if (!userIsLoggedIn && iterableRef.current) {
      iterableRef.current.logout();
      iterableRef.current = null;
      setIsIterableReady(false);
    }
  }, [userIsLoggedIn]);

  // Setup Pixel
  useEffect(() => {
    const pixel = new FacebookPixel({
      pixelID: REACT_APP_FACEBOOK_PIXEL_ID,
      debug: !IS_PROD,
      pageViewOnInit: true,
    });

    pixel.init({});

    setFacebookPixel(pixel);
  }, []);

  // Iterable in-browser messages
  // Fetch when app is loaded and when users navigate to a new page
  useEffect(() => {
    let cancelled = false;

    if (isIterableReady) {
      import("@iterable/web-sdk").then(({ getInAppMessages, DisplayOptions }) => {
        if (cancelled) return;

        const { request, triggerDisplayMessages } = getInAppMessages(
          {
            count: 20,
            displayInterval: 1000,
            packageName: REACT_APP_ITERABLE_APP_NAME,
          },
          { display: DisplayOptions.Deferred }
        );

        request()
          .then((response) => {
            if (cancelled) return;

            const unreadMessages = response.data.inAppMessages.filter(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (message: Record<string, any>) => !message.read
            );
            const countryRemovedPath = getCountryRemovedUrl(pathname);
            const defaultLocations = DEFAULT_NOTIFICATION_LOCATIONS.map((path: string) =>
              getCountryRemovedUrl(path)
            );
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const unreadMessagesForCurrentPath = unreadMessages.filter((message: Record<string, any>) => {
              const locations = message.customPayload?.webLocations || defaultLocations;
              return locations?.includes(countryRemovedPath);
            });

            triggerDisplayMessages(unreadMessagesForCurrentPath);
          })
          .catch((error: AxiosError) => {
            if (cancelled) return;

            if (error?.response) {
              // @ts-ignore
              const iterableCode = error.response?.data?.code || "";

              if (iterableCode === ERROR_CODE_ITERABLE_FORGOTTEN_USER) {
                setIsIterableReady(false);
                return;
              }

              if (iterableCode) {
                logError(`Iterable Error: ${iterableCode}`);
                return;
              }

              logError(
                `Iterable responded with status code: ${error.response?.status}, data: ${JSON.stringify(
                  error.response?.data
                )}`
              );
            }
          });
      });
    }

    return () => {
      cancelled = true;
    };
  }, [isIterableReady, pathname]);

  const formattedUserName = useMemo(() => {
    const { firstName, email } = memberLayoutInfoData?.me || {};
    const name = firstName || email || "";

    if (name.length <= HEADER_MENU_NAME_MAX_LENGTH) {
      return name;
    }
    const displayName = name.substring(0, HEADER_MENU_NAME_MAX_LENGTH);
    return `${displayName}...`;
  }, [memberLayoutInfoData?.me]);

  // Set up announcement popup
  useEffect(() => {
    const hasPromoCode =
      !!memberLayoutInfoData?.me?.shoppingCart?.discountCode ||
      !!guestMemberDiscountsData?.guestPromoCodeDiscount?.promoCode ||
      !!guestMemberReferralDiscountsData?.referralDiscount?.referralCode;

    if (hasPromoCode) {
      setIsAnnouncementPopupShown(false);
    } else if (showAnnouncementPopupForSubscribers && showAnnouncementPopupForNonSubscribers) {
      setIsAnnouncementPopupShown(true);
    } else if (showAnnouncementPopupForSubscribers || showAnnouncementPopupForNonSubscribers) {
      const subscription = memberLayoutInfoData?.me?.subscription;
      const hasSubscribedBefore =
        !!subscription &&
        subscription.subscriptionStatus.id !== SUBSCRIPTION_STATUS_NAME_TO_ID.NEVER_ACTIVATED;

      // Showing announcement popup to subscribed members
      if (hasSubscribedBefore && showAnnouncementPopupForSubscribers) setIsAnnouncementPopupShown(true);

      // Showing announcement popup to non-subscribed members
      if (!hasSubscribedBefore && showAnnouncementPopupForNonSubscribers) setIsAnnouncementPopupShown(true);
    }
  }, [
    guestMemberDiscountsData,
    guestMemberReferralDiscountsData,
    memberLayoutInfoData,
    showAnnouncementPopupForSubscribers,
    showAnnouncementPopupForNonSubscribers,
  ]);

  // Store the store location id in local storage
  useEffect(() => {
    const storeLocationId = checkIfRequestIsFromStoreLocation();
    const storeLocationIdInt = storeLocationId ? parseInt(storeLocationId, 10) : null;
    const existingStoreLocationId = getStoreLocationId();
    // Always store the store location id in local storage when present
    if (storeLocationId && storeLocationIdInt !== existingStoreLocationId) {
      window.localStorage.setItem(STORE_LOCATION_ID_STORAGE_KEY, storeLocationId);
    }

    if (
      isLoggedIn() &&
      storeLocationIdInt &&
      (!existingStoreLocationId || existingStoreLocationId !== storeLocationIdInt)
    ) {
      // Record the member physical store visit if the user is logged in.
      recordMemberPhysicalStoreVisit({
        variables: {
          input: {
            locationId: storeLocationIdInt,
          },
        },
        // @ts-ignore
      })
        .then((result) => {
          const { isSuccessful, errors } = result?.data?.recordMemberPhysicalStoreVisit || {};
          if (!isSuccessful || errors) {
            const error = errors?.[0]?.messages?.[0] || "";
            logError("Error recording member physical store visit.", error);
          }
        })
        .catch((error) => {
          logError("Error recording member physical store visit.", error);
        });
    }
  }, []);

  const showAnnouncementPopup = !isLoginPanelShown && !isBoxCartTrayOpened && isAnnouncementPopupShown;

  const isNative = isCapacitorNative();
  const disableHeaderComponents = (isSmall && checkIfRequestIsFromMobileApp()) || isNative;
  const shouldShowFooter = !disableHeaderComponents;
  const tabBarPresent = isNative && !shouldHideTabBar(pathname);
  const history = useHistory();

  const hasUnreadRecentNotifications = memberLayoutInfoData?.me?.hasUnreadRecentNotifications ?? false;
  const { me, minBottlesForFreeShipping, minBottlesInOrder, standardShippingFee, collections } =
    memberLayoutInfoData || {};
  const {
    id: memberId,
    points: memberTotalPoints,
    hasTakenTheQuiz: memberHasTakenTheQuiz,
    firstName: memberFirstName,
    hasDownloadedApp: memberHasDownloadedMobileApp,
    totalChargedOrdersExcludingGifts: memberTotalChargedPersonalOrders,
    level,
  } = me || {};
  const { rank: memberCurrentLevel, name: memberCurrentLevelName } = level || {};
  const shouldWinesBeAddedToSubscription = me?.shoppingCart?.shouldWinesBeAddedToSubscription ?? false;
  let totalPriceOnToggleButton;
  if (shouldWinesBeAddedToSubscription) {
    totalPriceOnToggleButton = memberLayoutInfoData?.me?.subscription?.netTotal ?? 0;
  } else {
    totalPriceOnToggleButton = userIsLoggedIn
      ? memberLayoutInfoData?.me?.shoppingCart?.total ?? 0
      : localShoppingCartData?.localShoppingCart?.totalProductsCost ?? 0;
  }

  const layoutContextValue = useMemo(
    () => ({
      setShowHeaderBarFromChild,
      setShouldOpenBoxAndCartTray,
      showHeaderBarFromChild,
      pushTopInsetBg,
      popTopInsetBg,
      pushTopInsetBgDefault,
      popTopInsetBgDefault,
    }),
    [
      showHeaderBarFromChild,
      shouldOpenBoxAndCartTray,
      pushTopInsetBg,
      popTopInsetBg,
      pushTopInsetBgDefault,
      popTopInsetBgDefault,
    ]
  );

  const appContextValue = useMemo(
    () => ({
      memberId,
      memberTotalPoints,
      memberHasTakenTheQuiz,
      memberFirstName,
      memberCurrentLevel,
      memberCurrentLevelName,
      memberHasDownloadedMobileApp,
      memberTotalChargedPersonalOrders,
      minBottlesForFreeShipping,
      minBottlesInOrder,
      standardShippingFee,
      shouldWinesBeAddedToSubscription,
      collections,
      isIterableReady,
      facebookPixel,
    }),
    [memberLayoutInfoData, isIterableReady, facebookPixel]
  );

  const isHeaderTransparent = variant === "home";
  const currentHeader = (
    <Header
      isHeaderTransparent={isHeaderTransparent}
      showAnnouncementBar={showAnnouncementBar}
      showBoxAndCartTray={showBoxAndCartTray}
      setHeightOfAnnouncement={handleChangeHeightOfAnnouncement}
      onClickSignIn={setIsLoginPanelShown}
      setHeaderDisplay={setIsHeaderShown}
      isHeaderShown={showHeaderBarFromChild || isHeaderShown}
      hasTabBarUnderHeader={hasTabBarUnderHeader}
      isBoxCartTrayOpened={isBoxCartTrayOpened}
      showHeaderBarFromChild={showHeaderBarFromChild}
      hasUnreadRecentNotifications={hasUnreadRecentNotifications}
      refetchMemberLayoutInfoData={refetchMemberLayoutInfo}
      firstName={formattedUserName}
      memberLayoutInfoData={memberLayoutInfoData}
    />
  );

  /* On the regular web build, the BoxAndCartTray is opened from the top
   * header (which we hide on Capacitor). On native, we surface the
   * tray's collapsed-state toggle bar directly — same UX as mobile RN's
   * `<BoxCartToggles>` over `<TabScreensNavBar>`: a persistent top
   * strip showing box + cart counts that opens the full tray on tap.
   * Hide the tray entirely on routes where the cart isn't relevant
   * (auth / quiz / checkout) so the toggle doesn't clutter those flows. */
  const shouldShowBoxAndCartTray =
    showBoxAndCartTray && (!disableHeaderComponents || (isNative && !shouldHideTabBar(pathname)));

  /* When the cart strip is visible at viewport y=0 the notch zone
   * needs to match its coral so the colour is continuous. Pushed to
   * the DEFAULT stack so any tray that opens over the cart strip and
   * has its own explicit hero colour (wine details' baseColor, login
   * panel's blue) wins. The cart strip remains the inset colour while
   * the dashboard is at viewport top with no tray covering it. */
  useEffect(() => {
    if (!shouldShowBoxAndCartTray) return undefined;
    const id = "layout-cart-strip";
    pushTopInsetBgDefault(id, colors.coral);
    return () => popTopInsetBgDefault(id);
  }, [shouldShowBoxAndCartTray, pushTopInsetBgDefault, popTopInsetBgDefault]);

  return (
    <AppContext.Provider value={appContextValue}>
      <LayoutContext.Provider value={layoutContextValue}>
        <GlobalStyle />
        <TopInsetSpacer $bg={activeTopInsetBg} />
        {userIsLoggedIn && (
          <Suspense fallback={null}>
            <RecommendationExplanationPoller />
          </Suspense>
        )}
        {!disableHeaderComponents && !hideHeader && currentHeader}
        <main
          className={cn("content", className)}
          /* The bottom-tab-bar offset is Capacitor-only — it's a
           * Capacitor-specific UI element that wouldn't exist in a PWA.
           * Top-side notch handling lives in `.content`'s GlobalStyle
           * @media (display-mode: standalone) rule; that path is shared
           * with any future PWA install. */
          style={
            tabBarPresent
              ? {
                  paddingBottom: `calc(${NATIVE_TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom))`,
                  minHeight: `calc(100vh - ${NATIVE_TAB_BAR_HEIGHT}px - env(safe-area-inset-bottom))`,
                }
              : undefined
          }
        >
          {shouldShowBoxAndCartTray && (
            <BoxHolder isHeaderShown={showHeaderBarFromChild || isHeaderShown}>
              {(memberLayoutInfoData?.me || !userIsLoggedIn) && (
                /* Gate on data presence, not on `loading`. First cold
                 * mount still waits for the initial fetch (preventing the
                 * one-off → monthly cart flash this gate was originally
                 * added for). But TOGGLE_SHOPPING_CART_SUBSCRIPTION refetches
                 * GET_MEMBER_LAYOUT_INFO via refetchQueries on every cart
                 * toggle, which flips `loading: true` for the duration of
                 * the network round-trip even though `data` still holds
                 * the previous state. Loading-based gating made the whole
                 * tray disappear during that window — invisible on web
                 * but visible on native through CapacitorHttp's slower
                 * round-trip.
                 *
                 * Logged-out visitors never get a `me` — GET_MEMBER_LAYOUT_INFO
                 * is requested with skipMe: !userIsLoggedIn, so `data.me` is
                 * skipped for guests. Gating on `me` alone would never mount
                 * the tray for them, hiding the guest one-off cart entirely
                 * (DEV-4465). They can't hit the flash this gate guards against
                 * either — guests are always in one-off/cart mode — and the
                 * tray's own internal guard hides it while the cart is empty,
                 * so mount it for them as soon as Layout renders. */
                <BoxAndCartTray
                  heightOfAnnouncement={heightOfAnnouncement}
                  shouldOpenBoxAndCartTray={shouldOpenBoxAndCartTray}
                  setShouldOpenBoxAndCartTray={setShouldOpenBoxAndCartTray}
                  showHeaderBarFromChild={showHeaderBarFromChild}
                  isHeaderShown={isHeaderShown}
                  pageHasOwnHeader={hideHeader}
                  setHeaderDisplay={setIsHeaderShown}
                  setBoxCartTrayDisplay={setIsBoxCartTrayOpened}
                  toggleWelcomePackWarning={handleToggleWelcomePackWarning}
                  showWelcomePackWarning={showWelcomePackWarning}
                  shouldWinesBeAddedToSubscription={shouldWinesBeAddedToSubscription}
                  totalPriceOnToggleButton={totalPriceOnToggleButton}
                  memberLayoutInfoData={memberLayoutInfoData}
                />
              )}
            </BoxHolder>
          )}
          {showOfferToChangeCountry && (
            <Suspense fallback={null}>
              <ChangeLanguagePopup closePopupHandler={() => setShowOfferToChangeCountry(false)} />
            </Suspense>
          )}
          {showAnnouncementPopup && !showOfferToChangeCountry && !showShippingDatesNotice && (
            <Suspense fallback={null}>
              <AnnouncementPopup closeAnnouncementHandler={() => setIsAnnouncementPopupShown(false)} />
            </Suspense>
          )}
          {/* Route-level Suspense boundary. While a lazy route chunk is still
              loading this fallback is shown instead of the page. The crawler
              prerenderer keys off the data-prerender-loading marker to know the
              route itself hasn't mounted yet (Layout flips window.prerenderReady
              as soon as its member-info query resolves, which can otherwise race
              the lazy chunk and capture a shell-only "husk"). The marker lives
              on a wrapper because StyledLoading doesn't forward DOM props — see
              netlify/functions/prerender.mts. */}
          <React.Suspense
            fallback={
              <div data-prerender-loading>
                <StyledLoading />
              </div>
            }
          >
            {isNative ? <PageTransition key={pathname}>{children}</PageTransition> : children}
          </React.Suspense>
        </main>
        {shouldShowFooter && !hideFooter && (
          <>
            <Footer />
            {shouldResumeBarBeShown() && (
              // Gate the lazy mount on actual saved-quiz state. Without this,
              // <Suspense> fires the chunk fetch on every page load even for
              // users who'd never see the bar — pulls ResumeQuizBar parse and
              // execute into the pre-LCP window for no benefit.
              <Suspense fallback={null}>
                <ResumeQuizBar />
              </Suspense>
            )}
          </>
        )}
        {isLoginPanelShown && (
          <Suspense fallback={null}>
            <LogInPanel loginPanelHandler={setIsLoginPanelShown} />
          </Suspense>
        )}
        {showWelcomePackWarning && (
          <Suspense fallback={null}>
            <ModalContainer
              isOpened={showWelcomePackWarning}
              outsideClickHandler={() => setShowWelcomePackWarning(false)}
              backgroundColor={colors.tanThin}
              isScrollLockEnabled={showWelcomePackWarning}
              fullViewMask
              isModalMdUpHeightAuto
              modalSidePadding={0}
            >
              <WelcomePackWarning
                history={history}
                toggleWelcomePackWarning={handleToggleWelcomePackWarning}
              />
            </ModalContainer>
          </Suspense>
        )}
        {showShippingDatesNotice && !showOfferToChangeCountry && (
          <ShippingDatesNoticeModal
            isOpened={showShippingDatesNotice}
            onClose={() => setShowShippingDatesNotice(false)}
          />
        )}
      </LayoutContext.Provider>
    </AppContext.Provider>
  );
};

const LayoutWithDelayNextBox = (props: LayoutProps) => (
  <DelayNextBoxProvider>
    <Layout {...props} />
  </DelayNextBoxProvider>
);

export default LayoutWithDelayNextBox;
